// Thin fetch wrapper for /api. `request` is used by every endpoint that
// requires an existing session: a 401 there means the session died mid-use,
// so it bounces the whole page to /login rather than leaving the caller to
// render a broken, half-authed view.
//
// Auth endpoints (login/signup/logout/me) go through raw `fetch` instead —
// for those, a 401 (or 400/409) is an expected, in-page result ("wrong
// password", "not logged in yet") to show inline, not a reason to redirect.

import type {
	ActiveProfile,
	ActiveSoloSummary,
	AddOrInviteResult,
	FriendsResponse,
	KidProfile,
	MatchesResponse,
	MatchState,
	Me,
	Plan,
	ProfileStats,
	PublicProfile,
	QuestionImage,
	SoloState,
	SoloSummary,
	Topic,
	User,
} from "../../types"

// Every rejection out of this module is an ApiError, so callers can branch on
// `status` instead of guessing from the message. `status` is the HTTP status,
// or 0 when the request never reached the server at all (see NETWORK_STATUS).
export class ApiError extends Error {
	status: number
	constructor(message: string, status: number) {
		super(message)
		this.status = status
	}
}

// The status we give a request that never got an HTTP response — offline, DNS,
// TLS, a dropped connection, a server that isn't listening. It is deliberately
// distinguishable from 401: a 401 means the session is gone, this means we do
// not know anything at all, so nothing may be concluded from it.
const NETWORK_STATUS = 0

// The browser's own message for a failed fetch is "Failed to fetch" (Chrome)
// or "Load failed" (Safari) — jargon that lands in a red alert in front of a
// player. Say the one thing that is both true and actionable instead.
const NETWORK_MESSAGE =
	"Can't reach the server — check your connection and try again."

// fetch() rejects only when the request never completed; a 4xx/5xx *response*
// resolves normally and is handled by the callers below. So this maps exactly
// the transport failures, and leaves real server-sent messages untouched.
async function doFetch(url: string, init?: RequestInit): Promise<Response> {
	try {
		return await fetch(url, init)
	} catch {
		throw new ApiError(NETWORK_MESSAGE, NETWORK_STATUS)
	}
}

// parseOrThrow is the response-parsing tail shared by request() and
// authRequest(): read the body (a non-JSON body parses as null rather than
// throwing), and turn a non-2xx status into an ApiError carrying the
// server's message when it has one.
async function parseOrThrow<T>(res: Response): Promise<T> {
	const data = await res.json().catch(() => null)
	if (!res.ok) {
		throw new ApiError(
			data?.error || `request failed (${res.status})`,
			res.status,
		)
	}
	return data as T
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
	const res = await doFetch(url, init)
	if (res.status === 401) {
		if (window.location.pathname !== "/login") {
			window.location.href = "/login"
		}
		throw new ApiError("unauthorized", 401)
	}
	return parseOrThrow<T>(res)
}

function get<T>(url: string): Promise<T> {
	return request<T>(url)
}

function post<T>(url: string, body?: unknown): Promise<T> {
	return request<T>(url, {
		method: "POST",
		headers:
			body === undefined ? undefined : { "Content-Type": "application/json" },
		body: body === undefined ? undefined : JSON.stringify(body),
	})
}

function del<T>(url: string): Promise<T> {
	return request<T>(url, { method: "DELETE" })
}

// --- auth (raw fetch, no auto-redirect) ------------------------------------

async function authRequest<T>(url: string, body?: unknown): Promise<T> {
	const res = await doFetch(url, {
		method: "POST",
		headers:
			body === undefined ? undefined : { "Content-Type": "application/json" },
		body: body === undefined ? undefined : JSON.stringify(body),
	})
	return parseOrThrow<T>(res)
}

// fetchMe checks whether a session cookie is present. Unlike `request`, a
// 401 here is a normal, expected outcome (not logged in yet) — it resolves
// to null instead of redirecting or throwing.
//
// Resolving null is therefore the *only* signal that means "signed out".
// Anything else — a 500, a proxy hiccup, an offline phone — rejects with an
// ApiError, and callers must not read that as a logged-out session: the
// session on the server is still perfectly good.
export async function fetchMe(): Promise<Me | null> {
	const res = await doFetch("/api/auth/me")
	if (res.status === 401) return null
	const data = await res.json().catch(() => null)
	if (!res.ok) {
		throw new ApiError(
			data?.error || `Failed to load session (${res.status})`,
			res.status,
		)
	}
	return (data as { user: Me }).user
}

// Signup is open to anyone. inviteToken is optional and only carries the
// friendship: an account created from a friend's invite link starts out
// friends with whoever sent it.
//
// It returns no user and sets no session, and that is deliberate rather than
// an oversight: signing up with an address that already has an account gets
// the identical answer, so nothing here — not a body, not a cookie — says
// whether the address was already registered. The mail that lands says it,
// to the one person entitled to know. So the only success state to render is
// "check your email".
export function signup(
	email: string,
	username: string,
	password: string,
	inviteToken: string,
): Promise<void> {
	return authRequest<{ ok: true }>("/api/auth/signup", {
		email,
		username,
		password,
		invite_token: inviteToken,
	}).then(() => undefined)
}

// verifyEmail consumes the token from the emailed link. It needs no session
// (the link is often opened in another browser), and like the other auth
// calls a 4xx here is an in-page result — an expired link — not a redirect.
export function verifyEmail(token: string): Promise<{ username: string }> {
	return authRequest<{ ok: true; username: string }>("/api/auth/verify", {
		token,
	})
}

export function resendVerification(): Promise<{ email: string }> {
	return authRequest<{ ok: true; email: string }>("/api/auth/verify/resend")
}

// requestPasswordReset asks for the "choose a new password" link. Like
// signup, it resolves to nothing on purpose: the server answers identically
// whether or not the address has an account, so there is nothing here to
// branch on and the only success state to render is "check your email".
export function requestPasswordReset(email: string): Promise<void> {
	return authRequest<{ ok: true }>("/api/auth/password/forgot", {
		email,
	}).then(() => undefined)
}

// resetPassword consumes the token from that link and sets the new password.
// It needs no session (the link is often opened on another device) and sets
// none either — the flow ends at the login form. A 4xx here is an in-page
// result — an expired or already-used link, a too-short password, or the
// rate limit — never a redirect.
export function resetPassword(
	token: string,
	newPassword: string,
): Promise<void> {
	return authRequest<{ ok: true }>("/api/auth/password/reset", {
		token,
		new_password: newPassword,
	}).then(() => undefined)
}

export function login(email: string, password: string): Promise<User> {
	return authRequest<{ user: User }>("/api/auth/login", {
		email,
		password,
	}).then((d) => d.user)
}

export async function logout(): Promise<void> {
	await authRequest("/api/auth/logout")
}

// --- topics -----------------------------------------------------------

export function fetchTopics(): Promise<Topic[]> {
	return get<Topic[]>("/api/topics")
}

// --- friends ------------------------------------------------------------

export function fetchFriends(): Promise<FriendsResponse> {
	return get<FriendsResponse>("/api/friends")
}

// addOrInviteFriend is the merged add/invite action: a username adds an
// existing member, an email adds a member or invites a non-member. Usernames
// are [a-z0-9_]{3,20} and can never contain "@", so the presence of an @ is
// a reliable discriminator (the backend enforces exactly-one-field too).
export function addOrInviteFriend(query: string): Promise<AddOrInviteResult> {
	const field = query.includes("@") ? { email: query } : { username: query }
	return post<AddOrInviteResult>("/api/friends", field)
}

export function acceptFriendRequest(id: number) {
	return post<{ ok: true }>(`/api/friends/${id}/accept`)
}

export function removeFriendship(id: number) {
	return del<{ ok: true }>(`/api/friends/${id}`)
}

// --- solo ---------------------------------------------------------------

export function createSoloPlay(
	topic: string,
	level: number,
): Promise<SoloState> {
	return post<SoloState>("/api/solo", { topic, level })
}

export function fetchSoloPlay(id: number): Promise<SoloState> {
	return get<SoloState>(`/api/solo/${id}`)
}

// A solo answer is one shape or the other, never both: an index into the
// choices, or the word built out of the tiles. Which one the server reads is
// decided by the stored question's kind.
export type SoloAnswer = { choice: number } | { text: string }

export function answerSoloQuestion(
	id: number,
	questionIdx: number,
	answer: SoloAnswer,
): Promise<SoloState> {
	return post<SoloState>(`/api/solo/${id}/answers`, {
		question_idx: questionIdx,
		...answer,
	})
}

export function fetchRecentSoloPlays(): Promise<SoloSummary[]> {
	return get<SoloSummary[]>("/api/solo")
}

// fetchActiveSoloPlays returns in-progress solo rounds to resume. Leaving a
// round mid-way discards nothing — the row stays 'active', and this is how
// Home offers it back.
export function fetchActiveSoloPlays(): Promise<ActiveSoloSummary[]> {
	return get<ActiveSoloSummary[]>("/api/solo/active")
}

// abandonSoloPlay throws an unfinished round away for good: the row is deleted
// server-side, so it leaves the resume list and counts towards no stat or
// badge. Only an active round can be abandoned — a finished one is a result.
export function abandonSoloPlay(id: number): Promise<{ ok: true }> {
	return del<{ ok: true }>(`/api/solo/${id}`)
}

// --- matches --------------------------------------------------------------

export function fetchMatches(): Promise<MatchesResponse> {
	return get<MatchesResponse>("/api/matches")
}
export function createMatch(
	topic: string,
	level: number,
	opponentId: number,
): Promise<MatchState> {
	return post<MatchState>("/api/matches", {
		topic,
		level,
		opponent_id: opponentId,
	})
}

export function acceptMatch(id: number): Promise<MatchState> {
	return post<MatchState>(`/api/matches/${id}/accept`)
}

export function declineMatch(id: number): Promise<MatchState> {
	return post<MatchState>(`/api/matches/${id}/decline`)
}

// forfeitMatch gives up an active match: it finishes immediately with the
// opponent as the winner. That is the point — a match only advances when both
// players have answered, so this is the one thing that unblocks an opponent
// left waiting on a player who is never coming back. Pending challenges are
// declined (declineMatch), not forfeited.
export function forfeitMatch(id: number): Promise<MatchState> {
	return post<MatchState>(`/api/matches/${id}/forfeit`)
}

export function answerMatchQuestion(
	id: number,
	questionIdx: number,
	choice: number,
): Promise<MatchState> {
	return post<MatchState>(`/api/matches/${id}/answers`, {
		question_idx: questionIdx,
		choice,
	})
}

// --- images -----------------------------------------------------------

// imageUrl maps a question's image descriptor to the static URL that serves
// it. build.mjs copies flag-icons' 4x3 flags to dist/flags/,
// frontend/assets/shapes/ to dist/shapes/, and frontend/assets/animals/ to
// dist/animals/, all mounted under /static/. Emoji images have no URL — the
// board renders them as text — so asking for one is a caller bug, not
// something to paper over with a placeholder.
export function imageUrl(image: QuestionImage): string {
	if (image.kind === "animal") {
		if (!image.slug) {
			throw new Error(`imageUrl: no asset URL for image kind "${image.kind}"`)
		}
		return `/static/animals/${image.slug}.webp`
	}
	if (image.kind === "emoji" || !image.iso) {
		throw new Error(`imageUrl: no asset URL for image kind "${image.kind}"`)
	}
	const dir = image.kind === "flag" ? "flags" : "shapes"
	return `/static/${dir}/${image.iso}.svg`
}

// --- profile --------------------------------------------------------------

export function updateProfileSettings(profilePublic: boolean) {
	return post<{ profile_public: boolean }>("/api/me/settings", {
		profile_public: profilePublic,
	})
}

export function changePassword(currentPassword: string, newPassword: string) {
	return post<{ ok: true }>("/api/me/password", {
		current_password: currentPassword,
		new_password: newPassword,
	})
}

export function fetchMyStats(): Promise<ProfileStats> {
	return get<ProfileStats>("/api/me/stats")
}

export function fetchPublicProfile(username: string): Promise<PublicProfile> {
	return get<PublicProfile>(`/api/users/${username}`)
}

// --- kid profiles ---------------------------------------------------------

// A profile is a named sub-identity under this account, for the owner's kids:
// the kids' topics only, its own solo progress, and no plan of its own (its
// rounds count against the owner's cap).

// The list is readable from a kid session too — it is what the switcher in
// KidNavbar reads to offer the way back to the grown-up.
export function fetchProfiles(): Promise<KidProfile[]> {
	return get<KidProfile[]>("/api/me/profiles")
}

// Owner sessions only (403 from a kid session). 409 when the name is already
// taken on this account, or when the account is at its profile limit — both
// arrive as an ApiError carrying the server's sentence.
export function createProfile(name: string): Promise<KidProfile> {
	return post<KidProfile>("/api/me/profiles", { name })
}

// Rename answers with the profile, like create does. Callers still update
// their list from the name they *sent*, so a row never depends on the round
// trip for text the user is already looking at.
export function renameProfile(id: number, name: string): Promise<KidProfile> {
	return post<KidProfile>(`/api/me/profiles/${id}`, { name })
}

// Deletes the profile and, by cascade, its plays — its badges and scores go
// with it. The rounds it played stay counted against the owner's cap.
export function deleteProfile(id: number): Promise<{ ok: true }> {
	return del<{ ok: true }>(`/api/me/profiles/${id}`)
}

// switchProfile sets who *this session* plays as; null goes back to the
// account owner. It is per session, so switching on the phone leaves every
// other signed-in device alone. Allowed from a kid session — that is how the
// phone gets handed back.
export function switchProfile(
	id: number | null,
): Promise<{ profile: ActiveProfile | null }> {
	return post<{ profile: ActiveProfile | null }>("/api/me/profiles/switch", {
		profile_id: id,
	})
}

// --- plan & billing ----------------------------------------------------------

export function fetchPlan(): Promise<Plan> {
	return get<Plan>("/api/me/plan")
}

// Both return a URL on Dodo's side for the browser to go to. A 402 never
// comes from these; it comes from starting a round (see isQuotaError).
export function startCheckout(): Promise<{ url: string }> {
	return post<{ url: string }>("/api/billing/checkout")
}

export function openBillingPortal(): Promise<{ url: string }> {
	return post<{ url: string }>("/api/billing/portal")
}
